import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, Download } from 'lucide-react';


import { searchAuditLogs, exportAuditLogs } from '../../services/auditService';

import FilterSidebar from './components/FilterSidebar';
import StatisticsPanel from './components/StatisticsPanel';
import AuditLogDetails from './components/AuditLogDetails';
import Icon from '../../components/AppIcon';


export default function ComprehensiveAuditTrail() {
  const [auditLogs, setAuditLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [statistics, setStatistics] = useState([]);
  const [userActivity, setUserActivity] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [actionDistribution, setActionDistribution] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    actionType: 'all',
    entityType: 'all',
    severityLevel: []
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);

  // Optimized data loading with Promise.all for instant rendering
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Single Promise.all call eliminates complex useEffect chains
      const [logs, stats] = await Promise.all([
        searchAuditLogs(searchTerm, filters),
        Promise.resolve(null) // placeholder for statistics
      ]);
      
      setAuditLogs(logs || []);
      setStatistics(stats || null);
    } catch (err) {
      setError(err?.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters]); // Minimal dependencies

  // Single useEffect for initial load - no complex chains
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Memoize filtering and pagination - prevent recalculation
  const { paginatedLogs, totalPages, filteredCount } = useMemo(() => {
    let filtered = [...auditLogs];
    
    // Apply filters
    if (searchTerm) {
      const searchLower = searchTerm?.toLowerCase();
      filtered = filtered?.filter(log =>
        log?.entity_name?.toLowerCase()?.includes(searchLower) ||
        log?.action_type?.toLowerCase()?.includes(searchLower) ||
        log?.user_email?.toLowerCase()?.includes(searchLower)
      );
    }
    
    if (filters?.actionType !== 'all') {
      filtered = filtered?.filter(log => log?.action_type === filters?.actionType);
    }
    
    if (filters?.entityType !== 'all') {
      filtered = filtered?.filter(log => log?.entity_type === filters?.entityType);
    }

    // Pagination
    const total = Math.ceil(filtered?.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginated = filtered?.slice(startIndex, startIndex + itemsPerPage);

    return {
      paginatedLogs: paginated,
      totalPages: total,
      filteredCount: filtered?.length
    };
  }, [auditLogs, searchTerm, filters, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  const handleSearch = async () => {
    if (!searchTerm?.trim()) {
      setFilteredLogs(auditLogs);
      return;
    }

    try {
      setLoading(true);
      const results = await searchAuditLogs(searchTerm);
      setFilteredLogs(results);
    } catch (error) {
      console.error('Error searching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleExport = async () => {
    try {
      await exportAuditLogs(filters);
    } catch (error) {
      console.error('Error exporting audit logs:', error);
    }
  };

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setShowDetails(true);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'CRITICAL': return 'text-red-600 bg-red-50';
      case 'ERROR': return 'text-orange-600 bg-orange-50';
      case 'WARNING': return 'text-yellow-600 bg-yellow-50';
      default: return 'text-blue-600 bg-blue-50';
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'CREATE': return '➕';
      case 'UPDATE': return '✏️';
      case 'DELETE': return '🗑️';
      case 'VIEW': return '👁️';
      case 'EXPORT': return '📥';
      case 'LOGIN': return '🔓';
      case 'LOGOUT': return '🔒';
      default: return '📋';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Trilha de Auditoria Completa</h1>
            <p className="text-sm text-gray-600 mt-1">
              Transparência total das operações financeiras com rastreamento completo
            </p>
          </div>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            Exportar Logs
          </button>
        </div>
      </div>
      <div className="flex h-[calc(100vh-120px)]">
        {/* Left Sidebar - Filters (20%) */}
        <FilterSidebar
          filters={filters}
          onFilterChange={handleFilterChange}
          userActivity={userActivity}
        />

        {/* Main Content - Audit Log Table (70%) */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Search Bar */}
          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  onKeyPress={(e) => e?.key === 'Enter' && handleSearch()}
                  placeholder="Buscar em todos os campos (usuário, ação, entidade, motivo...)"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={handleSearch}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Buscar
              </button>
            </div>

            {/* Active Filters Display */}
            {(filters?.actionType?.length > 0 || filters?.entityType?.length > 0 || filters?.severityLevel?.length > 0) && (
              <div className="flex items-center gap-2 mt-3">
                <span className="text-sm text-gray-600">Filtros ativos:</span>
                {filters?.actionType?.map(type => (
                  <span key={type} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                    {type}
                  </span>
                ))}
                {filters?.entityType?.map(type => (
                  <span key={type} className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                    {type}
                  </span>
                ))}
                {filters?.severityLevel?.map(level => (
                  <span key={level} className={`px-2 py-1 text-xs rounded-full ${getSeverityColor(level)}`}>
                    {level}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Audit Logs Table with Pagination */}
          <div className="p-6">
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-card/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Ação</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Entidade</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Usuário</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Data/Hora</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Motivo</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Nível</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Detalhes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {paginatedLogs?.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-6 py-12 text-center">
                          <p className="text-muted-foreground">Nenhum registro encontrado</p>
                        </td>
                      </tr>
                    ) : (
                      paginatedLogs?.map((log) => (
                        <tr key={log?.id} className="hover:bg-muted/50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-muted-foreground">{getActionIcon(log?.action_type)}</span>
                              <span className="text-sm font-medium text-muted-foreground">{log?.action_type}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-muted-foreground">{log?.entity_name}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-muted-foreground">{log?.user_email}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-muted-foreground">{log?.timestamp}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-muted-foreground">{log?.reason}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 text-xs rounded-full ${getSeverityColor(log?.status)}`}>
                              {log?.status}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 text-xs rounded-full ${getSeverityColor(log?.severity_level)}`}>
                              {log?.severity_level}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => handleViewDetails(log)}
                              className="text-sm text-primary hover:text-primary/80"
                            >
                              Ver Detalhes
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-border bg-muted/20">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, filteredCount)} de {filteredCount} registros
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                          currentPage === 1
                            ? 'text-muted-foreground cursor-not-allowed bg-muted/50'
                            : 'text-primary hover:bg-primary/10 border border-border'
                        }`}
                      >
                        <Icon name="ChevronLeft" size={16} />
                        <span>Anterior</span>
                      </button>
                      
                      <span className="text-sm text-muted-foreground">
                        Página {currentPage} de {totalPages}
                      </span>
                      
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                          currentPage === totalPages
                            ? 'text-muted-foreground cursor-not-allowed bg-muted/50'
                            : 'text-primary hover:bg-primary/10 border border-border'
                        }`}
                      >
                        <span>Próxima</span>
                        <Icon name="ChevronRight" size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Sidebar - Statistics (10%) */}
        <StatisticsPanel
          statistics={statistics}
          anomalies={anomalies}
          actionDistribution={actionDistribution}
        />
      </div>
      {/* Audit Log Details Modal */}
      {showDetails && selectedLog && (
        <AuditLogDetails
          log={selectedLog}
          onClose={() => setShowDetails(false)}
        />
      )}
    </div>
  );
}